/**
 * Central registry for opt-in, runtime-only debug toggles.
 *
 * Instead of adding a new `REACT_APP_*` build-time env var (or a one-off
 * localStorage key + window helper) every time we want a "show me the noisy
 * details" switch, add a new key to `DebugFlags` below and gate your
 * console.log/console.table with `isDebugFlagEnabled('yourFlag')`.
 *
 * Usage from the browser console:
 *   debug                      // inspect current flags, e.g. { mixpanel: false, tokenExpiry: false }
 *   debug.tokenExpiry = true   // turn a flag on (persists across reloads via localStorage)
 *   debug.tokenExpiry = false  // turn it off
 *
 * All flags default to `false`/off. Everything lives under a single
 * localStorage key so there's one place to clear/inspect state, and one file
 * (this one) to see every debug tool available in the app.
 */

// @code-guard: skip-next-line reason="type name for the debug-flags map, not a component"
export type DebugFlags = {
    /** Pretty-print every outgoing Mixpanel event/profile payload in the console. See src/utils/mixpanel.ts */
    mixpanel: boolean;
    /** Log seconds-left-until-expiry on every ensureFreshToken() call. See src/services/tokenManager.ts */
    tokenExpiry: boolean;
};

const DEFAULT_FLAGS: DebugFlags = {
    mixpanel: false,
    tokenExpiry: false,
};

const STORAGE_KEY = 'app_debug_flags';

function readStoredFlags(): Partial<DebugFlags> {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
}

function writeStoredFlags(flags: DebugFlags): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
    } catch {
        // Ignore quota / private-mode failures; flag just won't persist across reloads.
    }
}

const flagsState: DebugFlags = { ...DEFAULT_FLAGS, ...readStoredFlags() };

export const isDebugFlagEnabled = (flag: keyof DebugFlags): boolean => Boolean(flagsState[flag]);

/**
 * Registers `window.debug` as a live, settable view of `flagsState`. Reading
 * `debug` in the console shows the current flags; assigning to a property
 * (`debug.someFlag = true`) updates state, persists it, and logs the change.
 */
function registerDebugConsoleApi(): void {
    if (typeof window === 'undefined') return;

    const debugProxy = new Proxy(flagsState, {
        set(target, prop, value) {
            if (!(prop in DEFAULT_FLAGS)) {
                // @code-guard: skip-next-line reason="dev-facing console warning for an unknown debug flag name"
                console.warn(`[Debug] Unknown flag "${String(prop)}". Known flags:`, Object.keys(DEFAULT_FLAGS));
                return true;
            }
            target[prop as keyof DebugFlags] = Boolean(value);
            writeStoredFlags(target);
            // @code-guard: skip-next-line reason="dev-facing console confirmation when a debug flag is toggled"
            console.log(`%c[Debug] "${String(prop)}" -> ${Boolean(value)}`, 'color:#a259ff;font-weight:bold;');
            return true;
        },
    });

    (window as any).debug = debugProxy;
}

registerDebugConsoleApi();
